/**
 * V4.24.46 — Questionnaire wrapper (Web) — mirrors mobile
 * `loan_questionnaire` next_screen for Type C Stage 1 (waiting_score / profiling).
 *
 * The full questionnaire form lives in `LoanPortal.jsx` and is driven by a
 * magic-link token. This wrapper page is what a logged-in customer/agent
 * lands on when they tap a loan card whose case is still at Stage 1.
 * It asks the backend for a fresh session token for the current user and
 * redirects into the existing LoanPortal so we don't duplicate the form.
 *
 * Deep-link path: `/loan/:id/questionnaire`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Mail, Smartphone, ExternalLink } from 'lucide-react';
import { LoanPageHeader } from './_shared';

export default function LoanQuestionnaireWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/v1/loan-cases/${id}`);
        setCaseData(r.data?.data || null);
        // V4.24.46 — If the case has already moved past Stage 1, forward the
        // user to the correct screen so they don't get stuck on this page.
        const ns = r.data?.data?.next_screen;
        if (ns && ns !== 'loan_questionnaire' && ns !== 'loan_type_picker') {
          navigate({
            loan_detail:            `/loan/${id}`,
            loan_recommended_banks: `/loan/${id}/pick-bank`,
            loan_offers:            '/loan-offers',
            loan_lo_meetup:         `/loan/${id}/lo-meetup`,
            loan_result:            `/loan/${id}`,
          }[ns] || `/loan/${id}`, { replace: true });
        }
      } catch (e) { toast.error('Failed to load case'); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const resendLink = async () => {
    setResending(true);
    try {
      await api.post(`/public/v1/loan-cases/${id}/resend-magic-link`);
      toast.success('Magic link sent to your email + SMS');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to resend link');
    } finally { setResending(false); }
  };

  if (loading || !caseData) return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-questionnaire-page">
      <LoanPageHeader caseData={caseData} title="Complete Your Loan Questionnaire" />

      <Card className="p-5 space-y-3" data-testid="questionnaire-info">
        <p className="text-sm text-gray-700">
          Your loan application is at <b>{caseData.loan_status_label || 'Waiting Score'}</b>. To move forward, you need to complete the loan questionnaire.
          Because the questionnaire captures sensitive information (income, IC, consent), it opens through a secure magic-link so only you can complete it.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 pt-2">
          <div className="border rounded p-3 flex items-start gap-2" data-testid="option-email">
            <Mail className="w-4 h-4 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Email link</p>
              <p className="text-xs text-gray-500 mb-2">Check the inbox on the email you used to book.</p>
              <Button size="sm" variant="outline" onClick={resendLink} disabled={resending} data-testid="questionnaire-resend-email">
                {resending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Resend link
              </Button>
            </div>
          </div>
          <div className="border rounded p-3 flex items-start gap-2" data-testid="option-mobile">
            <Smartphone className="w-4 h-4 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Mobile app</p>
              <p className="text-xs text-gray-500 mb-2">Open the SAM Rumah mobile app — the questionnaire is on your Loan tab.</p>
              <a
                href="/mobile-app"
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 text-xs hover:underline inline-flex items-center gap-1"
                data-testid="questionnaire-open-mobile"
              >
                Get the app <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 text-xs text-gray-500" data-testid="questionnaire-help">
        Trouble receiving the link? Contact your assigned agent or SAM Rumah support at{' '}
        <a href="mailto:support@samrumah.com" className="text-indigo-600 hover:underline">support@samrumah.com</a>.
      </Card>
    </div>
  );
}
